import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './auth/hooks/useAuth';
import UnauthorizedPage from './auth/pages/UnauthorizedPage';
import dashboardRoutes from './routes/dashboardRoutes';
import uiRoutes from './routes/uiRoutes';
import authRoutes from './routes/authRoutes';
import calendarRoutes from './routes/calendarRoutes';
import profileRoutes from './routes/profileRoutes';
import taskRoutes from './routes/tasksRoutes';
import formsRoutes from './routes/formsRoutes';
import tablesRoutes from './routes/tablesRoutes';
import pagesRoutes from './routes/pagesRoutes';
import messagesRoutes from './routes/messagesRoutes';
import inboxRoutes from './routes/inboxRoutes';
import invoiceRoutes from './routes/invoiceRoutes';
import chartRoutes from './routes/chartRoutes';
import userRoutes from './routes/userRoutes';
import inventoryRoutes from './routes/inventoryRoutes';
import settingsRoutes from './routes/settingsRoutes';
import purchaseRoutes from './routes/purchaseRoutes';
import purchaseFiscalEventRoutes from './routes/purchaseFiscalEventRoutes';
import salesRoutes from './routes/salesRoutes';
import reportRoutes from './routes/reportRoutes';
import importRoutes from './routes/importRoutes';
import dteTemplateRoutes from './routes/dteTemplateRoutes';
import hardwareRoutes from './routes/hardwareRoutes';
import externalServicesRoutes from './routes/externalServicesRoutes';
import dteRoutes from './routes/dteRoutes';
import catalogRoutes from './routes/catalogRoutes';
import clientProfileRoutes from './routes/clientProfileRoutes';
import AlertDisplay from './components/Alerts/AlertDisplay';
import ModalDisplay from './components/Modals/ModalDisplay';
import SessionTimerDisplay from './components/SessionTimer/SessionTimerDisplay';
import NotificationDisplay from './components/Notifications/NotificationDisplay';
import LoadingModal from './components/Modals/LoadingModal';
import ConfirmModal from './components/Modals/ConfirmModal';
import RegisterWatchers from './watchers/RegisterWatchers';
import TopProgressBar from './components/Loading/TopProgressBar';
import ErrorPage from './pages/Pages/ErrorPage';
import cashRoutes from './routes/cashRoutes';
import authTemplatesRoutes from './routes/authTemplatesRoutes';
import uiConfigurationRoutes from './routes/uiConfigurationRoutes';
import aboutRoutes from './routes/aboutRoutes';
import qzTrayRoutes from './routes/qzTrayRoutes';
import PrintTicketModalDesignPreview from './components/Modals/PrintTicket/PrintTicketModal';
import promotionsRoutes from './routes/promotionsRoutes';
import priceListRoutes from './routes/priceListRoutes';
import accountsPayableRoutes from './routes/accountsPayableRoutes';
import accountsReceivableRoutes from './routes/accountsReceivableRoutes';
import GlobalModalsProvider from './components/GlobalModals/GlobalModalsProvider';
import AuthInitializer from './components/auth/AuthInitializer';
import { useAuthUser } from './hooks/auth/useAuthUser';
import { useEffect } from 'react';
import SaleDteProgressAlert from './components/sale/realtime/SaleDteProgressAlert';
import AppRealtimeRunner from './components/realtime/AppRealtimeRunner';
import PurchaseDteProgressAlert from './components/purchase/realtime/PurchaseDteProgressAlert';
import realtimeRoutes from './routes/realtimeRoutes';
import restaurantRoutes from './routes/restaurantRoutes';
import AppLayoutResolver from './layout/AppLayoutResolver';
import securityRoutes from './routes/securityRoutes';

export const AppRoute = () => {
  const { auth } = useAuth();
  const { fetchRoles, hasLoaded, isLoading: isLoadingRoles } = useAuthUser();
  const isAuth = !!auth?.accessToken && auth?.isAuthenticated;

  useEffect(() => {
    if (isAuth && !hasLoaded && !isLoadingRoles) fetchRoles();
  }, [isAuth, hasLoaded, isLoadingRoles]);

  return (
    <>
      {isAuth && (
        <>
          <AuthInitializer />

          <AppRealtimeRunner />

          <SaleDteProgressAlert />
          <PurchaseDteProgressAlert />

          <AlertDisplay />
          <ModalDisplay />
          <SessionTimerDisplay />
          <NotificationDisplay />
          <LoadingModal />
          <ConfirmModal />
          <RegisterWatchers />
          <TopProgressBar />
          <PrintTicketModalDesignPreview />

          <GlobalModalsProvider />
        </>
      )}

      <Routes>
        {isAuth ? (
          <Route element={<AppLayoutResolver />}>
            {/* ✅ Rutas principales protegidas */}
            {dashboardRoutes}
            {calendarRoutes}
            {profileRoutes}
            {taskRoutes}
            {formsRoutes}
            {tablesRoutes}
            {pagesRoutes}
            {messagesRoutes}
            {inboxRoutes}
            {invoiceRoutes}
            {chartRoutes}
            {uiRoutes}
            {userRoutes}
            {catalogRoutes}
            {inventoryRoutes}
            {settingsRoutes}
            {purchaseRoutes}
            {purchaseFiscalEventRoutes}
            {salesRoutes}
            {reportRoutes}
            {importRoutes}
            {dteTemplateRoutes}
            {hardwareRoutes}
            {externalServicesRoutes}
            {dteRoutes}
            {clientProfileRoutes}
            {cashRoutes}
            {authTemplatesRoutes}
            {uiConfigurationRoutes}
            {aboutRoutes}
            {qzTrayRoutes}
            {promotionsRoutes}
            {priceListRoutes}
            {accountsPayableRoutes}
            {accountsReceivableRoutes}
            {realtimeRoutes}
            {restaurantRoutes}
            {securityRoutes}

            {/* 🚫 Ruta sin permiso */}
            <Route path="/unauthorized" element={<UnauthorizedPage />} />

            {/* ❌ Ruta inexistente (404 real) */}
            <Route path="*" element={<ErrorPage />} />
          </Route>
        ) : (
          <>
            {authRoutes}
            {/* 🔁 Si no está autenticado, redirigir a login */}
            <Route path="*" element={<Navigate to="/auth/signin" replace />} />
          </>
        )}
      </Routes>
    </>
  );
};

export default AppRoute;
